import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  Contact,
  CreateContact,
  UpdateContact,
} from "@property-reports/api-contract";

const API_BASE = import.meta.env.VITE_API_URL || "";

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    credentials: "include",
  });
  if (!response.ok) {
    // Try to extract error message from response body
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      if (errorBody.message) {
        errorMessage = errorBody.message;
      }
    } catch {
      // JSON parsing failed, use default message
    }
    throw new Error(errorMessage);
  }
  if (response.status === 204) {
    return null as T;
  }
  return response.json();
}

export function useContacts() {
  return useQuery({
    queryKey: ["contacts"],
    queryFn: () => fetchJson<Contact[]>(`${API_BASE}/api/contacts`),
  });
}

export function useContact(id: string) {
  return useQuery({
    queryKey: ["contact", id],
    queryFn: () => fetchJson<Contact>(`${API_BASE}/api/contacts/${id}`),
    enabled: !!id,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { body: CreateContact }) =>
      fetchJson<Contact>(`${API_BASE}/api/contacts`, {
        method: "POST",
        body: JSON.stringify(data.body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { params: { id: string }; body: UpdateContact }) =>
      fetchJson<Contact>(`${API_BASE}/api/contacts/${data.params.id}`, {
        method: "PATCH",
        body: JSON.stringify(data.body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { params: { id: string } }) =>
      fetchJson<null>(`${API_BASE}/api/contacts/${data.params.id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}
